import { useState } from 'react'
import { Link } from 'react-router-dom'
import compassTilted from '../assets/icons/componentLibrary/compass-tilted.svg'
import compassLg from '../assets/icons/componentLibrary/compass-lg.svg'
import mapExplore from '../assets/icons/componentLibrary/map-explore.svg'
import mapPinFilled from '../assets/icons/componentLibrary/map-pin-filled.svg'
import gift from '../assets/icons/componentLibrary/gift.svg'
import starFilled from '../assets/icons/componentLibrary/star-filled.svg'
import lightning from '../assets/icons/componentLibrary/lightning.svg'
import './Landing.css'

const FAQS = [
  {
    q: 'Is it free to play?',
    a: 'Yes — Loot Seeker is completely free for players. Browse, start, and complete as many hunts as you like. Businesses sponsor the hunts and fund the rewards, so adventuring never costs you a thing.',
  },
  {
    q: 'How are waypoints kept safe?',
    a: 'All waypoints are reviewed by our team before going live. We only approve public, accessible locations — storefronts, parks, plazas, and other safe, well-lit areas. Sponsors must agree to our safety guidelines before creating a hunt.',
  },
  {
    q: 'Can I create my own hunt?',
    a: "Player-created hunts are on our roadmap! Right now, hunts are created by verified business sponsors using our Hunt Creator Studio. Sign up and we'll notify you when community hunts launch.",
  },
  {
    q: 'How do businesses get started?',
    a: 'Click "Become a sponsor," complete a short profile, and our onboarding team will reach out within 24 hours. We\'ll walk you through setting up your first hunt — most sponsors go live in under a week.',
  },
]

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  return (
    <div className="lp">
      {/* ══ NAVIGATION ══ */}
      <header>
        <nav className="nav">
          <div className="nav-inner">
            <Link className="nav-brand" to="/">
              <img className="nav-brand-icon" src={compassTilted} alt="Compass" />
              <span className="nav-wordmark">Loot Seeker</span>
            </Link>
            <ul className="nav-links">
              <li>
                <a href="#hunts">Browse hunts</a>
              </li>
              <li>
                <a href="#sponsors">For business</a>
              </li>
              <li>
                <Link to="/login" className="nav-signin">
                  Sign in
                </Link>
              </li>
              <li>
                <Link to="/register" className="nav-cta">
                  Sign up
                </Link>
              </li>
            </ul>
            <button
              className="nav-hamburger"
              aria-label="Open menu"
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div className={`nav-mobile${mobileOpen ? ' open' : ''}`}>
            <a href="#hunts" onClick={() => setMobileOpen(false)}>
              Browse hunts
            </a>
            <a href="#sponsors" onClick={() => setMobileOpen(false)}>
              For business
            </a>
            <Link to="/login">Sign in</Link>
            <Link to="/register" className="nav-mobile-cta-link">
              Sign up — it's free
            </Link>
          </div>
        </nav>
      </header>

      {/* ══ HERO ══ */}
      <section className="hero">
        <svg
          className="hero-topo"
          viewBox="0 0 1440 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <path d="M-100 550 Q200 480 400 520 Q600 560 800 490 Q1000 420 1200 470 Q1350 510 1550 460" stroke="#d97706" strokeWidth="1.5" fill="none" opacity="0.4" />
          <path d="M-100 480 Q180 410 380 450 Q580 490 780 420 Q980 350 1200 400 Q1380 440 1550 390" stroke="#d97706" strokeWidth="1.5" fill="none" opacity="0.35" />
          <path d="M-100 410 Q160 340 360 380 Q560 420 760 350 Q960 280 1180 330 Q1360 370 1550 320" stroke="#b45309" strokeWidth="1" fill="none" opacity="0.28" />
          <path d="M-100 340 Q140 270 340 310 Q540 350 740 280 Q940 210 1160 260 Q1340 300 1550 250" stroke="#b45309" strokeWidth="1" fill="none" opacity="0.22" />
          <path d="M-100 270 Q120 200 320 240 Q520 280 720 210 Q920 140 1140 190 Q1320 230 1550 180" stroke="#92400e" strokeWidth="1" fill="none" opacity="0.16" />
          <path d="M-100 200 Q100 130 300 170 Q500 210 700 140 Q900 70 1120 120 Q1300 160 1550 110" stroke="#92400e" strokeWidth="1" fill="none" opacity="0.12" />
          <path d="M200 600 Q300 520 350 450 Q400 380 320 300 Q260 240 280 170" stroke="#d97706" strokeWidth="1" fill="none" opacity="0.2" />
          <path d="M600 600 Q660 510 680 430 Q700 350 640 270 Q590 200 620 120" stroke="#d97706" strokeWidth="1" fill="none" opacity="0.18" />
          <path d="M1000 600 Q1040 510 1050 430 Q1060 350 1020 270 Q990 200 1010 120" stroke="#b45309" strokeWidth="1" fill="none" opacity="0.18" />
          <ellipse cx="720" cy="300" rx="380" ry="220" stroke="#d97706" strokeWidth="1" fill="none" opacity="0.07" />
          <ellipse cx="720" cy="300" rx="550" ry="340" stroke="#d97706" strokeWidth="1" fill="none" opacity="0.05" />
        </svg>

        <div className="hero-content">
          <div className="hero-eyebrow">
            <span className="hero-eyebrow-dot"></span>
            Now live in 40+ cities
          </div>
          <h1 className="hero-headline">
            Hunt your city.
            <br />
            <em>Claim real loot.</em>
          </h1>
          <p className="hero-subhead">
            Follow clues to real places, solve them, win rewards from local businesses.
          </p>
          <div className="hero-actions">
            <Link to="/hunts" className="btn-pill-primary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Start hunting
            </Link>
            <a href="#sponsors" className="btn-pill-outline">
              Become a sponsor
            </a>
          </div>
        </div>

        <div className="hero-scroll" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section className="section" id="how">
        <div className="section-inner">
          <div className="section-label">How it works</div>
          <h2 className="section-title">Three steps to adventure</h2>
          <p className="section-sub">
            No gear needed. Just your phone, your curiosity, and a good pair of shoes.
          </p>

          <div className="how-grid">
            <div className="how-card">
              <div className="how-num">01</div>
              <div className="how-icon">
                <img src={mapExplore} alt="Map" />
              </div>
              <h3>Pick a hunt near you</h3>
              <p>
                Browse active hunts in your city filtered by difficulty, distance, and reward type.
                New hunts drop every week from local sponsors.
              </p>
            </div>
            <div className="how-card">
              <div className="how-num">02</div>
              <div className="how-icon">
                <img src={mapPinFilled} alt="Pin" />
              </div>
              <h3>Follow clues to real locations</h3>
              <p>
                Each hunt leads you through a series of waypoints — real storefronts, parks, and
                landmarks. Solve the riddle at each stop to advance.
              </p>
            </div>
            <div className="how-card">
              <div className="how-num">03</div>
              <div className="how-icon">
                <img src={gift} alt="Reward" />
              </div>
              <h3>Claim the loot</h3>
              <p>
                Finish the final waypoint and unlock your reward — a discount code, free item, or
                exclusive deal from the sponsoring business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FEATURED HUNTS ══ */}
      <section className="section hunts-section" id="hunts">
        <div className="section-inner">
          <div className="section-label">Featured hunts</div>
          <h2 className="section-title">What's running near you</h2>
          <p className="section-sub">Hundreds of hunts active right now. Here's a taste.</p>

          <div className="hunts-grid">
            <Link to="/hunts" className="hunt-card">
              <div className="hunt-card-img">
                <div
                  className="hunt-card-img-bg"
                  style={{ background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 50%, #d97706 100%)' }}
                >
                  🏛️
                </div>
                <div className="hunt-overlay"></div>
                <div className="hunt-badge-row">
                  <span className="lp-badge-easy">🌿 Easy</span>
                  <span className="hunt-reward-badge">🎁 12 of 50 left</span>
                </div>
              </div>
              <div className="hunt-card-body">
                <div className="hunt-meta">
                  <span className="hunt-waypoints">
                    <img src={mapPinFilled} alt="Waypoints" />5 waypoints
                  </span>
                </div>
                <div className="hunt-title">Old Town Treasure Trail</div>
                <div className="hunt-sponsor">
                  Sponsored by <strong>Harbor Coffee Co.</strong>
                </div>
              </div>
            </Link>

            <Link to="/hunts" className="hunt-card">
              <div className="hunt-card-img">
                <div
                  className="hunt-card-img-bg"
                  style={{ background: 'linear-gradient(135deg, #d0fae5 0%, #34d399 50%, #059669 100%)' }}
                >
                  🌿
                </div>
                <div className="hunt-overlay"></div>
                <div className="hunt-badge-row">
                  <span className="lp-badge-medium">⚡ Medium</span>
                  <span className="hunt-reward-badge">🎁 8 of 30 left</span>
                </div>
              </div>
              <div className="hunt-card-body">
                <div className="hunt-meta">
                  <span className="hunt-waypoints">
                    <img src={mapPinFilled} alt="Waypoints" />8 waypoints
                  </span>
                </div>
                <div className="hunt-title">The Riverside Riddle</div>
                <div className="hunt-sponsor">
                  Sponsored by <strong>Greenway Market</strong>
                </div>
              </div>
            </Link>

            <Link to="/hunts" className="hunt-card">
              <div className="hunt-card-img">
                <div
                  className="hunt-card-img-bg"
                  style={{ background: 'linear-gradient(135deg, #ffe4e6 0%, #fb7185 50%, #be123c 100%)' }}
                >
                  🔥
                </div>
                <div className="hunt-overlay"></div>
                <div className="hunt-badge-row">
                  <span className="lp-badge-hard">🔥 Hard</span>
                  <span className="hunt-reward-badge">🎁 3 of 20 left</span>
                </div>
              </div>
              <div className="hunt-card-body">
                <div className="hunt-meta">
                  <span className="hunt-waypoints">
                    <img src={mapPinFilled} alt="Waypoints" />
                    12 waypoints
                  </span>
                </div>
                <div className="hunt-title">Midnight Market Heist</div>
                <div className="hunt-sponsor">
                  Sponsored by <strong>The Night Owl Bistro</strong>
                </div>
              </div>
            </Link>
          </div>

          <div className="hunts-cta">
            <Link to="/hunts" className="btn-pill-primary">
              Browse all hunts
            </Link>
          </div>
        </div>
      </section>

      {/* ══ FOR SPONSORS ══ */}
      <section className="sponsors-section" id="sponsors">
        <div className="sponsors-inner">
          <div className="sponsors-illustration">
            <div className="sponsors-illo-wrap">
              <img className="sponsors-illo-compass" src={compassLg} alt="Compass" />
              <div className="sponsors-illo-stats">
                <div className="sponsors-illo-stat">
                  <div className="sponsors-illo-stat-val">247</div>
                  <div className="sponsors-illo-stat-lab">Starts</div>
                </div>
                <div className="sponsors-illo-stat">
                  <div className="sponsors-illo-stat-val">189</div>
                  <div className="sponsors-illo-stat-lab">Claims</div>
                </div>
                <div className="sponsors-illo-stat">
                  <div className="sponsors-illo-stat-val">94%</div>
                  <div className="sponsors-illo-stat-lab">Converted</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sponsors-copy">
            <div className="sponsors-eyebrow">For local businesses</div>
            <h2 className="sponsors-title">
              Bring <em>real customers</em>
              <br />
              to your door.
            </h2>
            <ul className="sponsors-bullets">
              <li className="sponsors-bullet">
                <div className="sponsors-bullet-icon">
                  <img src={starFilled} alt="" />
                </div>
                <div className="sponsors-bullet-text">
                  <strong>Branded hunts, your way</strong>
                  <span>
                    Design a hunt route that ends at your venue. Your logo, your story, your
                    customers.
                  </span>
                </div>
              </li>
              <li className="sponsors-bullet">
                <div className="sponsors-bullet-icon">
                  <img src={gift} alt="" />
                </div>
                <div className="sponsors-bullet-text">
                  <strong>Redeemable promo codes</strong>
                  <span>
                    Set a reward — a free drink, 20% off, anything — and we handle the code
                    delivery.
                  </span>
                </div>
              </li>
              <li className="sponsors-bullet">
                <div className="sponsors-bullet-icon">
                  <img src={lightning} alt="" />
                </div>
                <div className="sponsors-bullet-text">
                  <strong>Live dashboard analytics</strong>
                  <span>
                    Track hunt starts, completions, and redemptions in real time. Know your ROI
                    instantly.
                  </span>
                </div>
              </li>
            </ul>
            <Link to="/register?role=business" className="btn-pill-primary">
              Become a sponsor
            </Link>
          </div>
        </div>
      </section>

      {/* ══ TRUST STRIP ══ */}
      <section className="trust-section">
        <div className="trust-grid">
          <div className="trust-item">
            <div className="trust-num">1,200+</div>
            <div className="trust-label">Hunts available</div>
          </div>
          <div className="trust-item">
            <div className="trust-num">84K</div>
            <div className="trust-label">Players joined</div>
          </div>
          <div className="trust-item">
            <div className="trust-num">210K</div>
            <div className="trust-label">Rewards claimed</div>
          </div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section className="section faq-section" id="faq">
        <div className="section-inner">
          <div className="section-label">Questions</div>
          <h2 className="section-title">Frequently asked</h2>
          <p className="section-sub" style={{ marginBottom: 'var(--lp-space-10)' }}>
            Everything you need to know before your first hunt.
          </p>

          <div className="faq-list">
            {FAQS.map((item, i) => {
              const open = openFaq === i
              return (
                <div key={i} className={`faq-item${open ? ' open' : ''}`}>
                  <button
                    className="faq-q"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? null : i)}
                  >
                    {item.q}
                    <svg
                      className="faq-chevron"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  <div className="faq-a">{item.a}</div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div>
              <div className="footer-brand">
                <img src={compassTilted} alt="Compass" />
                <span>Loot Seeker</span>
              </div>
              <p className="footer-tagline">
                The location-based treasure hunt platform where local businesses bring players
                through the door.
              </p>
              <div className="footer-socials">
                <a className="footer-social" href="#" aria-label="Twitter">
                  𝕏
                </a>
                <a className="footer-social" href="#" aria-label="Instagram">
                  ▲
                </a>
                <a className="footer-social" href="#" aria-label="TikTok">
                  ♪
                </a>
              </div>
            </div>

            <div>
              <div className="footer-col-title">Product</div>
              <ul className="footer-links">
                <li>
                  <a href="#hunts">Browse hunts</a>
                </li>
                <li>
                  <a href="#how">How it works</a>
                </li>
                <li>
                  <Link to="/login">Leaderboard</Link>
                </li>
                <li>
                  <Link to="/login">My rewards</Link>
                </li>
              </ul>
            </div>

            <div>
              <div className="footer-col-title">Company</div>
              <ul className="footer-links">
                <li>
                  <a href="#">About</a>
                </li>
                <li>
                  <a href="#sponsors">For business</a>
                </li>
                <li>
                  <a href="#">Blog</a>
                </li>
                <li>
                  <a href="#">Careers</a>
                </li>
              </ul>
            </div>

            <div>
              <div className="footer-col-title">Legal</div>
              <ul className="footer-links">
                <li>
                  <a href="#">Terms of service</a>
                </li>
                <li>
                  <a href="#">Privacy policy</a>
                </li>
                <li>
                  <a href="#">Cookie settings</a>
                </li>
                <li>
                  <a href="#">Accessibility</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <span className="footer-copy">© 2026 Loot Seeker Inc. All rights reserved.</span>
            <Link to="/login" className="footer-signin">
              Sign in →
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
